import { LitElement, css, html, nothing, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import sheet from './css/bb-cart.css?inline'
import { asFulfillment } from './bbcore/hints'
import type { Hint } from './bbcore/types'

// ── Price math (string-preserving) ────────────────────────────────────
// The relay surfaces money as a pre-formatted string (`"R36.00"`, `"$5"`, …)
// — BerryCore's `price.formatted` is the only thing the cart carries. We
// can't ship a localiser/Intl pipeline for every storefront, so we split the
// string into prefix + amount + suffix, do the arithmetic, and reassemble in
// the same shape we got it in. Round-tripping is the contract:
// `formatPrice(parsePrice(s)!) === s` for any well-formed input. Mixed
// currencies in `sumPrices` return null — we'd rather show nothing than
// invent a total. (Exported for unit testing — these are pure functions.)

export interface ParsedPrice {
  prefix: string
  amount: number
  decimals: number
  suffix: string
}

// Matches optional non-digit prefix, a numeric body (integer or decimal),
// and an optional non-digit suffix. Commas inside the number are treated as
// decimal separators (e.g. "1,50 €") via the `replace` below.
const PRICE_RX = /^(\D*?)(\d+(?:[.,]\d+)?)(\D*)$/

export function parsePrice(s: string): ParsedPrice | null {
  const m = PRICE_RX.exec(s.trim())
  if (!m) return null
  const [, prefix, numStr, suffix] = m
  const normalised = numStr.replace(',', '.')
  const amount = parseFloat(normalised)
  if (!Number.isFinite(amount)) return null
  const dot = normalised.indexOf('.')
  return {
    prefix,
    amount,
    decimals: dot >= 0 ? normalised.length - dot - 1 : 0,
    suffix,
  }
}

export function formatPrice(p: ParsedPrice): string {
  return `${p.prefix}${p.amount.toFixed(p.decimals)}${p.suffix}`
}

export function multiplyPrice(s: string, n: number): string | null {
  const p = parsePrice(s)
  return p ? formatPrice({ ...p, amount: p.amount * n }) : null
}

// Sum a list of formatted prices. Returns null if the list is empty, any
// entry is unparseable, or the currencies (prefix/suffix) disagree — those
// are the cases where producing a number would be misleading.
export function sumPrices(items: string[]): string | null {
  if (!items.length) return null
  const parsed = items.map(parsePrice)
  if (parsed.some((p) => p === null)) return null
  const ps = parsed as ParsedPrice[]
  const head = ps[0]
  if (ps.some((p) => p.prefix !== head.prefix || p.suffix !== head.suffix)) {
    return null
  }
  return formatPrice({
    prefix: head.prefix,
    amount: ps.reduce((s, p) => s + p.amount, 0),
    decimals: Math.max(...ps.map((p) => p.decimals)),
    suffix: head.suffix,
  })
}

// Cart payloads come in several shapes from BerryBot. Normalise into a
// uniform `{name, quantity, modifiers[], removed?}` tree so the
// renderer below stays simple.
interface CartLine {
  name: string
  quantity?: number
  // The line's *unit* price (what BerryCore sent on the menu item).
  price?: string
  // Unit × quantity, computed client-side. Falls back to `price` when we
  // can't parse it or when quantity is 1. Null if there's no price at all.
  lineTotal?: string | null
  removed?: boolean
  // Per-modifier metadata from the serializer (Trello #139). `groupName`
  // is the modifier group label ("Add Mayo Inside?"). `modifierType` is
  // 'choice' | 'addition' | 'removal' — drives the row glyph and the
  // strike-through. Both undefined on the top-level cart line.
  groupName?: string
  modifierType?: string
  children: CartLine[]
}

interface NormalisedCart {
  lines: CartLine[]
  total: string
  // Client-computed sum of every line's `line_total`. Only set when *every*
  // line carries a parseable price and they share a currency — `sumPrices`
  // returns null otherwise and we hide the row.
  subtotal: string | null
  // Server-side breakdown surfaced once BerryBot has the inputs: tax once
  // a fulfillment_type is set + the storefront has a tax rate;
  // delivery_fee once the customer confirms a delivery address. Each is
  // an empty string while its inputs are missing, in which case the row
  // hides — that's the contract with `compute_totals` on the server.
  tax: string
  deliveryFee: string
}

function normaliseCart(raw: unknown): NormalisedCart {
  if (!raw || typeof raw !== 'object')
    return { lines: [], total: '', subtotal: null, tax: '', deliveryFee: '' }
  const cart = raw as Record<string, unknown>
  const arr = Array.isArray(cart)
    ? (cart as Array<Record<string, unknown>>)
    : Array.isArray(cart.items)
      ? (cart.items as Array<Record<string, unknown>>)
      : []
  const lines = arr
    .map((it): CartLine | null => {
      const name = String(it.name ?? it.item ?? '')
      if (!name) return null
      const nested = Array.isArray(it.items)
        ? (it.items as Array<Record<string, unknown>>).map(normaliseChild)
        : [
            ...((it.options as Array<unknown> | undefined) ?? []).map(toChild),
            ...((it.extras as Array<unknown> | undefined) ?? []).map(toChild),
            ...((it.removable_ingredients as Array<unknown> | undefined) ?? []).map(
              (n) => ({ ...toChild(n), removed: true }),
            ),
          ]
      const quantity =
        typeof it.quantity === 'number' ? (it.quantity as number) : undefined
      const price = typeof it.price === 'string' ? (it.price as string) : undefined
      // Per-line total = (unit price × quantity) + sum of non-removed
      // children's prices. Mirrors backend `cart.py:_line_cents` so the
      // zero-priced-base + priced-modifier pattern (e.g. a smoothie whose
      // price lives entirely on the required "Size" modifier) renders the
      // real total instead of R0.00.
      const lineTotal = computeLineTotal(price, quantity, nested)
      return { name, quantity, price, lineTotal, children: nested }
    })
    .filter((x): x is CartLine => x !== null)
  const total =
    typeof cart.grand_total === 'string'
      ? cart.grand_total
      : typeof cart.subtotal === 'string'
        ? cart.subtotal
        : ''
  // Only show a client-computed Subtotal when every line carries a
  // parseable, same-currency price; otherwise fall back to the server's
  // `total` (rendered separately below) and hide the row.
  const lineTotals = lines.map((l) => l.lineTotal)
  const subtotal = lineTotals.every((t): t is string => typeof t === 'string')
    ? sumPrices(lineTotals as string[])
    : null
  const tax = typeof cart.tax === 'string' ? cart.tax : ''
  const deliveryFee =
    typeof cart.delivery_fee === 'string' ? cart.delivery_fee : ''
  return { lines, total, subtotal, tax, deliveryFee }
}

// Compose the per-line total: (base × qty) + each non-removed child's
// price. Returns null when nothing is parseable (so the UI hides the
// line-total rather than showing a misleading partial). Mirrors backend
// cart.py:_line_cents but is intentionally more forgiving — a zero-base
// item that has only modifier prices still surfaces a total.
export function computeLineTotal(
  basePrice: string | undefined,
  quantity: number | undefined,
  children: CartLine[],
): string | null {
  const childPrices = children
    .filter((c) => !c.removed && typeof c.price === 'string')
    .map((c) => c.price as string)
  const qty = quantity && quantity > 0 ? quantity : 1
  const baseLine =
    basePrice !== undefined
      ? qty > 1
        ? (multiplyPrice(basePrice, qty) ?? basePrice)
        : basePrice
      : null
  const parts = [baseLine, ...childPrices].filter(
    (p): p is string => typeof p === 'string',
  )
  if (!parts.length) return null
  return sumPrices(parts) ?? baseLine ?? null
}

function normaliseChild(it: Record<string, unknown>): CartLine {
  const modifierType =
    typeof it.modifier_type === 'string' ? (it.modifier_type as string) : undefined
  return {
    name: String(it.name ?? ''),
    quantity: typeof it.quantity === 'number' ? (it.quantity as number) : undefined,
    // Capture the modifier's own price — needed by `lineTotal` so a
    // zero-priced base item (e.g. a smoothie whose only price is on the
    // required "Size" modifier) still surfaces a meaningful line total.
    price: typeof it.price === 'string' ? (it.price as string) : undefined,
    // Treat the explicit `removed: true` from the serializer as the
    // strike-through trigger; also recognise modifier_type='removal' for
    // forward-compat if the bool is ever dropped.
    removed: it.removed === true || modifierType === 'removal',
    ...(typeof it.group_name === 'string' && it.group_name
      ? { groupName: it.group_name as string }
      : {}),
    ...(modifierType ? { modifierType } : {}),
    children: [],
  }
}

function toChild(n: unknown): CartLine {
  return { name: String(n ?? ''), children: [] }
}

// Per-modifier row inside a cart line (Trello #139). Renders:
//   <glyph> <group label>: <option>          <+price>
// Glyph is driven by modifier_type ('addition' → +, 'removal' → ✕,
// 'choice' → no glyph); the row's removed class still drives the
// strike-through. Group label is shown when present and non-trivial
// (skip "" or "Modifiers" — they add noise, not signal). Price column
// only renders when the modifier has its own non-zero price string.
function renderModifier(c: CartLine) {
  const glyph =
    c.modifierType === 'addition'
      ? '+'
      : c.modifierType === 'removal'
        ? '✕'
        : ''
  const showGroup = c.groupName && c.groupName.toLowerCase() !== 'modifiers'
  // Hide the price when it's a free choice (R0/blank). Additions surface
  // theirs because that's the explanation for the line total uplift.
  const showPrice =
    c.price && !/^[A-Za-z$€£¥]*\s*0(?:[.,]0+)?\s*$/.test(c.price)
  return html`<li class=${c.removed ? 'removed' : ''}>
    <span class="mod-text">
      ${glyph ? html`<span class="mod-glyph mod-${c.modifierType}">${glyph}</span>` : nothing}
      ${showGroup ? html`<span class="mod-group">${c.groupName}:</span>` : nothing}
      <span class="mod-name">${c.name}</span>
    </span>
    ${showPrice
      ? html`<span class="mod-price">${c.modifierType === 'addition' ? '+' : ''}${c.price}</span>`
      : nothing}
  </li>`
}

function totalQty(lines: CartLine[]): number {
  return lines.reduce((s, l) => s + (l.quantity ?? 1), 0)
}

// Cart line head price: show the item's base × quantity, excluding
// modifier deltas. Each modifier renders as its own priced sub-row
// below the line, so folding the delta into the head produced the
// ambiguous "is the +R8 added on top of R66, or already in it?"
// reading Adriana flagged (Trello #165). Falls back to `null` when
// there's no parseable non-zero base — the zero-priced-base +
// priced-modifier case (e.g. a smoothie whose only price lives on the
// Size choice) still needs `lineTotal` on the head, otherwise the
// line looks free.
function lineHeadPrice(l: CartLine): string | null {
  if (!l.price) return null
  const parsed = parsePrice(l.price)
  if (!parsed || parsed.amount === 0) return null
  const qty = l.quantity && l.quantity > 1 ? l.quantity : 1
  return qty > 1 ? (multiplyPrice(l.price, qty) ?? l.price) : l.price
}

@customElement('bb-cart')
export class BbCart extends LitElement {
  static styles = css`${unsafeCSS(sheet)}`

  // ── Schema (identical for every variant) ──────────────────────────
  // (api / conversationId props removed — the controller owns the
  // /orders/submit call now; CartPanel just bubbles a submit-order event.)
  @property({ attribute: false })
  conversationId: string | null = null

  @property({ attribute: false })
  cart: unknown = null

  @property({ attribute: false })
  fulfillmentType: string | null = null

  @property({ attribute: false })
  hint: Hint | null = null

  // True while a controller-level mutation (cart add/remove, fulfillment,
  // send) is in flight — disables the per-line qty controls so a customer
  // can't queue racing mutations.
  @property({ attribute: false })
  busy = false

  // ── Variant config ────────────────────────────────────────────────
  @property({ type: Boolean, reflect: true })
  collapsible = false

  // When set with `collapsible`, the cart renders as a full-width
  // sticky strip instead of a 48px floating circle. Used by mobile
  // layout so the qty badge sits in the widget's visible flow
  // (above the active panel) regardless of which tab is active.
  @property({ type: Boolean, reflect: true })
  compact = false

  // External-binding mode. When set, the cart looks up the named
  // <bb-widget> by id, subscribes to its `bb-state-change` event to
  // mirror cart/fulfillment/hint/busy/complete onto its own props, and
  // forwards its own click events (cart-add / cart-remove / cart-clear /
  // submit-order) to the widget's public methods. Unset = legacy mode
  // (the widget itself sets props + listens for events in its template).
  @property({ type: String, reflect: true, attribute: 'widget' })
  widgetId: string | null = null

  // Set once we've successfully bound to a <bb-widget>. Used by
  // disconnectedCallback to know whether to tear listeners back down.
  private _boundWidget: HTMLElement | null = null
  private _onWidgetStateChange = (e: Event) => {
    const s = (e as CustomEvent).detail
    if (!s || typeof s !== 'object') return
    // Push each field individually so Lit's hasChanged short-circuits
    // identical-value writes (the bb-state-change event fires on every
    // widget render, not only on actual cart-state mutations).
    this.conversationId = s.conversationId ?? null
    this.cart = s.cart ?? null
    this.fulfillmentType = s.fulfillmentType ?? null
    this.hint = s.hint ?? null
    this.busy = !!s.busy
    this.complete = !!s.complete
  }
  private _onSelfCartAdd = (e: Event) => {
    const detail = (e as CustomEvent).detail as
      | { itemName?: string; quantity?: number }
      | undefined
    const w = this._boundWidget as unknown as {
      cartAdd?: (n: string, q?: number) => Promise<void>
    }
    if (detail?.itemName && w?.cartAdd) void w.cartAdd(detail.itemName, detail.quantity)
  }
  private _onSelfCartRemove = (e: Event) => {
    const detail = (e as CustomEvent).detail as
      | { itemName?: string; quantity?: number }
      | undefined
    const w = this._boundWidget as unknown as {
      cartRemove?: (n: string, q?: number) => Promise<void>
    }
    if (detail?.itemName && w?.cartRemove) {
      void w.cartRemove(detail.itemName, detail.quantity)
    }
  }
  private _onSelfCartClear = () => {
    const w = this._boundWidget as unknown as { clearCart?: () => Promise<void> }
    if (w?.clearCart) void w.clearCart()
  }
  private _onSelfSubmitOrder = () => {
    const w = this._boundWidget as unknown as { submitOrder?: () => Promise<void> }
    if (w?.submitOrder) void w.submitOrder()
  }

  @state() private expanded = true

  // Driven by the controller's `conversationState === 'complete'` — the
  // widget passes it in. Renders the post-submit "Order placed ✓" label
  // on the button (the full thank-you screen replaces the chat panel at
  // the widget level; this just keeps the cart-button copy honest).
  @property({ attribute: false })
  complete = false

  private onDocClick = (e: MouseEvent) => {
    if (!this.collapsible || !this.expanded) return
    if (!e.composedPath().includes(this)) this.expanded = false
  }
  private onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.collapsible && this.expanded) {
      this.expanded = false
    }
  }

  connectedCallback() {
    super.connectedCallback()
    if (this.collapsible) this.expanded = false
    document.addEventListener('mousedown', this.onDocClick)
    document.addEventListener('keydown', this.onKey)
    if (this.widgetId) void this._bindExternal()
  }

  disconnectedCallback() {
    document.removeEventListener('mousedown', this.onDocClick)
    document.removeEventListener('keydown', this.onKey)
    this._unbindExternal()
    super.disconnectedCallback()
  }

  // Wait for <bb-widget> to be defined, then look up the target. The
  // host page may declare bb-cart before bb-widget in document order, or
  // before the widget script has finished loading.
  private async _bindExternal() {
    if (!this.widgetId) return
    if (typeof customElements !== 'undefined') {
      try {
        await customElements.whenDefined('bb-widget')
      } catch {
        // ignore — fall through to the getElementById attempt
      }
    }
    if (!this.isConnected || !this.widgetId) return
    const root = this.getRootNode() as Document | ShadowRoot
    const widget =
      (root as Document).getElementById?.(this.widgetId) ??
      document.getElementById(this.widgetId)
    if (!widget) {
      console.warn(`[bb-cart] no <bb-widget> with id="${this.widgetId}"`)
      return
    }
    this._boundWidget = widget
    widget.addEventListener('bb-state-change', this._onWidgetStateChange)
    this.addEventListener('cart-add', this._onSelfCartAdd)
    this.addEventListener('cart-remove', this._onSelfCartRemove)
    this.addEventListener('cart-clear', this._onSelfCartClear)
    this.addEventListener('submit-order', this._onSelfSubmitOrder)
    // Pull current state immediately so the cart isn't blank until the
    // next widget render. `state` is part of the widget's public surface.
    const seed = (widget as unknown as { state?: unknown }).state
    if (seed) {
      this._onWidgetStateChange(
        new CustomEvent('bb-state-change', { detail: seed }),
      )
    }
  }

  private _unbindExternal() {
    const w = this._boundWidget
    if (!w) return
    w.removeEventListener('bb-state-change', this._onWidgetStateChange)
    this.removeEventListener('cart-add', this._onSelfCartAdd)
    this.removeEventListener('cart-remove', this._onSelfCartRemove)
    this.removeEventListener('cart-clear', this._onSelfCartClear)
    this.removeEventListener('submit-order', this._onSelfSubmitOrder)
    this._boundWidget = null
  }

  willUpdate(changed: Map<string, unknown>) {
    // If collapsible turns on after mount, start collapsed.
    if (changed.has('collapsible') && this.collapsible && changed.get('collapsible') === false) {
      this.expanded = false
    }
    // Auto-collapse when a fulfillment hint appears — the cart would
    // otherwise overlay the fulfillment affordance and hide the choice
    // the user has to make next.
    if (changed.has('hint') && this.collapsible && this.expanded && asFulfillment(this.hint)) {
      this.expanded = false
    }
  }

  private dispatchAdd(itemName: string) {
    this.emitAffordance('cart-add-row', { item: itemName })
    this.dispatchEvent(
      new CustomEvent('cart-add', {
        detail: { itemName, quantity: 1 },
        bubbles: true,
        composed: true,
      }),
    )
  }

  private dispatchRemove(itemName: string, quantity?: number) {
    this.emitAffordance(
      quantity ? 'cart-remove-one' : 'cart-remove-all',
      { item: itemName },
    )
    this.dispatchEvent(
      new CustomEvent('cart-remove', {
        detail: quantity ? { itemName, quantity } : { itemName },
        bubbles: true,
        composed: true,
      }),
    )
  }

  private emitAffordance(
    kind: string,
    context?: Record<string, unknown>,
  ) {
    this.dispatchEvent(
      new CustomEvent('widget-affordance', {
        detail: { kind, ...(context ? { context } : {}) },
        bubbles: true,
        composed: true,
      }),
    )
  }

  // Bubble a "clear the whole cart" intent — the widget routes it to
  // controller.clearCart(), which loops the relay's per-item /cart/remove
  // (there is no clear-cart endpoint).
  private clear() {
    if (this.busy || this.complete) return
    this.emitAffordance('cart-clear')
    this.dispatchEvent(
      new CustomEvent('cart-clear', { bubbles: true, composed: true }),
    )
  }

  // Bubble a `submit-order` intent up to the widget — the controller
  // owns the actual `/orders/submit` call now, so it can flip its own
  // `submitting` flag and `conversationState`, driving the widget-level
  // overlay + thank-you screen. CartPanel just shows the local
  // "Order placed ✓" affordance label, slaved off the controller's
  // conversationState (passed via the `complete` prop).
  private submit() {
    if (this.complete || this.busy) return
    this.emitAffordance('submit-cart')
    this.dispatchEvent(
      new CustomEvent('submit-order', {
        bubbles: true,
        composed: true,
      }),
    )
  }

  private renderToggle(qty: number, show: boolean, total: string) {
    return html`
      <button
        class="toggle"
        ?hidden=${!show}
        @click=${() => {
          this.expanded = true
          this.emitAffordance('cart-expand')
        }}
        aria-label="Open cart"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z"
          />
        </svg>
        <span class="badge" ?hidden=${qty === 0}>${qty}</span>
        ${this.compact
          ? html`<span class="strip-label">
              ${qty === 0
                ? 'Cart is empty'
                : html`${qty} item${qty === 1 ? '' : 's'}${total ? html` · <b>${total}</b>` : nothing}`}
            </span>
            <span class="strip-chev" aria-hidden="true">▸</span>`
          : nothing}
      </button>
    `
  }

  private renderLine(l: CartLine) {
    return html`
      <div class="line">
        <div class="line-head">
          <span class="qty-controls">
            <button
              class="add"
              ?disabled=${this.busy}
              @click=${() => this.dispatchAdd(l.name)}
              aria-label=${`Add another ${l.name}`}
            >+</button>
            ${l.quantity && l.quantity > 1
              ? html`<button
                  class="remove decrement"
                  ?disabled=${this.busy}
                  @click=${() => this.dispatchRemove(l.name, 1)}
                  aria-label=${`Remove one ${l.name}`}
                >−</button>`
              : html`<button
                  class="remove"
                  ?disabled=${this.busy}
                  @click=${() => this.dispatchRemove(l.name)}
                  aria-label=${`Remove ${l.name}`}
                >×</button>`}
          </span>
          <span class="line-name">
            ${l.quantity && l.quantity > 1 ? `${l.quantity}× ` : ''}${l.name}
          </span>
          ${(() => {
            const meta = lineHeadPrice(l) ?? l.lineTotal ?? l.price
            return meta
              ? html`<span class="line-meta">${meta}</span>`
              : nothing
          })()}
        </div>
        ${l.children.length
          ? html`<ul class="modifiers">
              ${l.children.map((c) => renderModifier(c))}
            </ul>`
          : nothing}
      </div>
    `
  }

  // One `Subtotal` / `Tax` / `Delivery fee` row — same shape, `cls` distinguishes
  // them in CSS. (The `Total` row uses a different class and is rendered inline.)
  private renderTotalsRow(label: string, value: string, cls = '') {
    return html`<div class="subtotal ${cls}"><span>${label}</span><span>${value}</span></div>`
  }

  render() {
    const { lines, total, subtotal, tax, deliveryFee } = normaliseCart(this.cart)
    const fulfillment = this.fulfillmentType?.toLowerCase()
    const fulfillmentLabel =
      fulfillment === 'pick_up' || fulfillment === 'pickup'
        ? 'Pickup'
        : fulfillment === 'delivery'
          ? 'Delivery'
          : null
    // "Submit" / "Clear cart" are offered whenever there's something to act on
    // and the cart isn't mid-action or already submitted. Submit stays enabled
    // even without fulfillment — clicking it then routes through chat so the
    // agent surfaces the fulfillment hint (rather than 422-ing on submit).
    const actionable = lines.length > 0 && !this.busy && !this.complete
    const canSubmit = actionable
    const submitLabel = this.complete
      ? 'Order placed ✓'
      : this.busy
        ? 'Submitting…'
        : 'Submit Order'

    const showPanel = !this.collapsible || this.expanded

    return html`
      ${this.renderToggle(totalQty(lines), this.collapsible && !this.expanded, subtotal ?? total ?? '')}

      <div class="panel" ?hidden=${!showPanel}>
        <div class="header">
          <span>My Cart</span>
          <span class="header-actions">
            <button
              class="clear-cart"
              ?hidden=${!actionable}
              @click=${this.clear}
              aria-label="Clear cart"
            >
              Clear
            </button>
            <button
              class="close"
              ?hidden=${!this.collapsible}
              @click=${() => {
                this.expanded = false
                this.emitAffordance('cart-collapse')
              }}
              aria-label="Close cart"
            >
              ✕
            </button>
          </span>
        </div>
        <div class="body">
          ${lines.length === 0
            ? html`<div class="empty">Your cart is empty</div>`
            : lines.map((l) => this.renderLine(l))}
        </div>
        <div class="footer">
          ${fulfillmentLabel
            ? html`<div class="fulfillment">Fulfillment: ${fulfillmentLabel}</div>`
            : nothing}
          ${subtotal ? this.renderTotalsRow('Subtotal', subtotal) : nothing}
          ${tax ? this.renderTotalsRow('Tax', tax, 'tax') : nothing}
          ${deliveryFee ? this.renderTotalsRow('Delivery fee', deliveryFee, 'delivery-fee') : nothing}
          ${total
            ? html`<div class="totals"><span>Total</span><span>${total}</span></div>`
            : nothing}
          <button class="submit" ?disabled=${!canSubmit} @click=${this.submit}>
            ${this.busy && !this.complete
              ? html`<span
                    class="bb-btn-spinner"
                    part="btn-spinner"
                    aria-hidden="true"
                  ></span>${submitLabel}`
              : submitLabel}
          </button>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bb-cart': BbCart
  }
}
